/**
 * Common TypeScript type definitions for CheckerFly application
 *
 * **Purpose**: Provide type-safe alternatives to `any` types, following WI-028 standards.
 *
 * **Compliance**:
 * - CLAUDE.md: "**NEVER use `any`** - Use these alternatives"
 * - WI-028: TypeScript Type Safety Improvements
 *
 * @see WI-028 for TypeScript standards
 */

/**
 * Network Information API type definitions
 *
 * Used by SelectivePreloadStrategy (WI-019) to make network-aware preloading decisions.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
 */
export interface NavigatorConnection {
  /**
   * Effective connection type based on observed round-trip time and downlink values.
   *
   * - 'slow-2g': < 50 Kbps (skip preloading)
   * - '2g': ~50-70 Kbps (skip preloading)
   * - '3g': ~700 Kbps (skip preloading)
   * - '4g': >= 700 Kbps (preload enabled)
   * - 'unknown': Connection type cannot be determined (preload enabled for graceful degradation)
   */
  effectiveType: 'slow-2g' | '2g' | '3g' | '4g' | 'unknown';
}

/**
 * Extended Navigator interface with optional connection property
 *
 * The Network Information API is not available in all browsers:
 * - Chrome 61+: Supported
 * - Firefox 31+: Supported
 * - Safari: Not supported (as of iOS 17/macOS 14)
 * - IE/Edge Legacy: Not supported
 *
 * Code using this type should always check if connection exists before accessing.
 */
export interface NavigatorWithConnection extends Navigator {
  /**
   * Network Information API connection object
   *
   * **Undefined** in browsers that don't support Network Information API (e.g., Safari).
   * Always check for existence:
   *
   * ```typescript
   * const connection = (navigator as NavigatorWithConnection).connection;
   * if (connection) {
   *   const effectiveType = connection.effectiveType;
   * }
   * ```
   */
  connection?: NavigatorConnection;
}

/**
 * Firebase error type (re-exported from Firebase SDK)
 *
 * **Usage**:
 * ```typescript
 * import { FirebaseError } from '@core/types/common.types';
 *
 * try {
 *   await firebaseOperation();
 * } catch (error) {
 *   if (error instanceof FirebaseError) {
 *     console.error('Firebase error:', error.code, error.message);
 *   }
 * }
 * ```
 */
export { FirebaseError } from 'firebase/app';

/**
 * Firebase Auth settings type for emulator configuration
 *
 * **Usage**: Type assertion for Firebase Auth emulator settings
 * **Context**: WI-028 - TypeScript Type Safety Improvements
 *
 * Firebase SDK doesn't expose this property in public types, but it exists
 * in the internal implementation for emulator testing.
 *
 * @see app.config.ts - Firebase Auth emulator configuration
 */
export interface FirebaseAuthSettings {
  appVerificationDisabledForTesting?: boolean;
}

/**
 * Extended Auth type with settings property
 *
 * **Usage**: Type-safe access to Firebase Auth internal settings
 *
 * @example
 * ```typescript
 * const auth = getAuth();
 * (auth as AuthWithSettings).settings.appVerificationDisabledForTesting = true;
 * ```
 */
export interface AuthWithSettings {
  settings: FirebaseAuthSettings;
}

/**
 * Generic error with code property (Firebase-style errors)
 *
 * **Usage**: Type guard for errors with code property
 *
 * @example
 * ```typescript
 * catch (error: unknown) {
 *   if (isErrorWithCode(error)) {
 *     console.log(error.code);
 *   }
 * }
 * ```
 */
export interface ErrorWithCode {
  code?: string;
  message: string;
}

/**
 * Type guard for ErrorWithCode
 *
 * @param error - Error to check
 * @returns true if error has code property
 */
export function isErrorWithCode(error: unknown): error is ErrorWithCode {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as ErrorWithCode).code === 'string'
  );
}

/**
 * Component with optional blur method
 *
 * **Usage**: CanDeactivate guard component typing
 *
 * @see blur-on-leave.guard.ts
 */
export interface ComponentWithBlur {
  onBlur?: () => void;
}

/**
 * Test-only types
 *
 * Note: SpyObj type is NOT exported from this file to avoid production build errors.
 * Use `jasmine.SpyObj<T>` directly in test files.
 *
 * **Usage in tests**:
 * ```typescript
 * const mockService: jasmine.SpyObj<MyService> = jasmine.createSpyObj('MyService', ['method1']);
 * ```
 */
